import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import type {
  CouplingLoadView,
  UsinageDetail,
} from "@/features/milling/api/types"
import {
  buildCouplageReport,
  reportSourceKey,
} from "@/features/milling/lib/couplage-report"
import { formatNumber } from "@/lib/format"
import { cn } from "@/lib/utils"

interface CouplageReportPanelProps {
  usinage: UsinageDetail
  loads: CouplingLoadView[]
}

// On-screen Couplage matrix — chargement × lot, design-system
// styled. Always rendered open (no collapsible). Download actions
// live in the parent PageHeader, not on this card — the panel is
// just the live read of the matrix as lots are fabricated.
export function CouplageReportPanel({
  usinage,
  loads,
}: CouplageReportPanelProps) {
  const { t } = useTranslation("milling")
  const data = useMemo(
    () => buildCouplageReport(usinage, loads),
    [usinage, loads],
  )

  return (
    <Card className="flex flex-col gap-0 overflow-hidden p-0">
      {/* Header band — title only; downloads moved to PageHeader. */}
      <div className="border-b bg-muted/40 px-4 py-3">
        <h3 className="text-sm font-semibold tracking-tight">
          {t("couplageReport.title")}
        </h3>
        <p className="text-xs text-muted-foreground tabular-nums">
          {t("couplageReport.headerSubtitle", {
            usinageNumber: data.usinageNumber,
            from: data.dateRange.from,
            to: data.dateRange.to,
          })}
        </p>
      </div>

      {/* Single wide table — left ledger + lot columns + RESTE. */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          {/* Two-tier head: outer band split into "(left ledger)" +
              "N° des lots produits". */}
          <thead>
            <tr className="border-b bg-muted/30 text-muted-foreground">
              <th
                className="px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide"
                colSpan={5}
              >
                {t("couplageReport.leftHeader")}
              </th>
              <th
                className="border-l px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide"
                colSpan={data.lots.length + 1}
              >
                {t("couplageReport.lotsHeader")}
              </th>
            </tr>
            <tr className="border-b bg-muted/20 text-muted-foreground">
              <th className={HEAD_CELL}>
                {t("rapport.cols.fournisseur")}
              </th>
              <th className={HEAD_CELL}>
                {t("rapport.cols.cccNumber")}
              </th>
              <th className={cn(HEAD_CELL, "text-right")}>
                {t("couplageReport.cols.nbreSacs")}
              </th>
              <th className={cn(HEAD_CELL, "text-right")}>
                {t("couplageReport.cols.poidsNet")}
              </th>
              <th className={cn(HEAD_CELL, "text-right")}>
                {t("couplageReport.cols.poidsMoyenne")}
              </th>
              {data.lots.map((l) => (
                <th
                  key={l.id}
                  className={cn(HEAD_CELL, "border-l text-center")}
                >
                  <span className="font-mono">{l.label}</span>
                </th>
              ))}
              <th
                className={cn(
                  HEAD_CELL,
                  "border-l text-right text-foreground",
                )}
              >
                {t("couplageReport.cols.reste")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.sources.length === 0 ? (
              <tr>
                <td
                  colSpan={5 + data.lots.length + 1}
                  className="py-6 text-center text-xs text-muted-foreground"
                >
                  {t("couplageReport.emptySources")}
                </td>
              </tr>
            ) : (
              data.sources.map((s) => {
                const key = reportSourceKey(s.kind, s.id)
                const reste = data.resteBySource[key] ?? 0
                return (
                  <tr key={key}>
                    <td className={BODY_CELL}>
                      {s.kind === "QUEUE_LOT" ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] font-normal"
                        >
                          {s.fournisseur}
                        </Badge>
                      ) : (
                        s.fournisseur
                      )}
                    </td>
                    <td className={cn(BODY_CELL, "font-mono text-[11px]")}>
                      {s.cccNumber}
                    </td>
                    <td className={cn(BODY_CELL, "text-right tabular-nums")}>
                      {formatNumber(s.nbreSacs)}
                    </td>
                    <td className={cn(BODY_CELL, "text-right tabular-nums")}>
                      {formatNumber(s.poidsNetKg, 2)}
                    </td>
                    <td className={cn(BODY_CELL, "text-right tabular-nums")}>
                      {formatNumber(s.poidsMoyenneKg, 2)}
                    </td>
                    {data.lots.map((l) => {
                      const bags = l.bagsBySource[key] ?? 0
                      return (
                        <td
                          key={l.id}
                          className={cn(
                            BODY_CELL,
                            "border-l text-right tabular-nums",
                          )}
                        >
                          {bags > 0 ? formatNumber(bags) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </td>
                      )
                    })}
                    <td
                      className={cn(
                        BODY_CELL,
                        "border-l text-right tabular-nums",
                      )}
                    >
                      {reste > 0 ? (
                        formatNumber(reste)
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
          <tfoot>
            {/* TOTAL SACS row — only planned totals. The couplage
                report is a planning artifact; per-lot actual weights
                live in the Freinte rapport (CompletedView). */}
            <tr className="bg-muted/40 text-foreground">
              <td
                colSpan={2}
                className={cn(
                  BODY_CELL,
                  "text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
                )}
              >
                {t("couplageReport.totalSacs")}
              </td>
              <td
                className={cn(
                  BODY_CELL,
                  "text-right font-semibold tabular-nums",
                )}
              >
                {formatNumber(data.totalNbreSacs)}
              </td>
              <td
                className={cn(
                  BODY_CELL,
                  "text-right font-semibold tabular-nums",
                )}
              >
                {formatNumber(data.totalPoidsNetKg, 2)}
              </td>
              <td
                className={cn(
                  BODY_CELL,
                  "text-right font-semibold tabular-nums",
                )}
              >
                {formatNumber(data.totalPoidsMoyenneKg, 2)}
              </td>
              {data.lots.map((l) => (
                <td
                  key={l.id}
                  className={cn(
                    BODY_CELL,
                    "border-l text-right font-semibold tabular-nums",
                  )}
                >
                  {formatNumber(data.totalBagsByLot[l.id] ?? 0)}
                </td>
              ))}
              <td
                className={cn(
                  BODY_CELL,
                  "border-l text-right font-semibold tabular-nums",
                )}
              >
                {formatNumber(data.totalResteSacs)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  )
}

const HEAD_CELL =
  "h-9 px-3 text-left text-[10px] font-medium uppercase tracking-wide"
const BODY_CELL = "px-3 py-2 text-xs"
